/**
 * Renovação de sessão coordenada.
 *
 * Motivo: várias telas pediam `refreshSession()`/`getSession()` de forma independente
 * (e em várias abas), gerando dezenas de renovações por minuto. Cada renovação rotaciona
 * o token e invalida o anterior — o excesso gera 429 e tokens inválidos, derrubando o login.
 *
 * Aqui garantimos: single-flight em memória, trava entre abas (Web Locks),
 * intervalo mínimo entre renovações e backoff em 429.
 */
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

const LAST_REFRESH_KEY = "fabchat:last-auth-refresh";
const LOCK_NAME = "fabchat-auth-refresh";
const MIN_REFRESH_INTERVAL_MS = 20_000;
const EXPIRY_MARGIN_S = 60;

let inFlight: Promise<Session | null> | null = null;
let backoffUntil = 0;

export type AuthErrorKind = "credential" | "rate_limit" | "network" | "unknown";

/** Classifica erros de auth para decidir se a sessão local deve ser descartada. */
export function classifyAuthError(error: unknown): AuthErrorKind {
  if (!error) return "unknown";
  const err = error as { status?: number; code?: string; message?: string; name?: string };
  const status = typeof err.status === "number" ? err.status : undefined;
  const message = (err.message || "").toLowerCase();
  const code = (err.code || "").toLowerCase();

  if (status === 429 || code === "over_request_rate_limit" || message.includes("rate limit")) {
    return "rate_limit";
  }
  if (
    err.name === "AbortError" ||
    message.includes("failed to fetch") ||
    message.includes("network") ||
    message.includes("timeout") ||
    message.includes("timed out")
  ) {
    return "network";
  }
  if (status === 401 || status === 403 || code.includes("jwt") || code === "session_not_found") {
    return "credential";
  }
  return "unknown";
}

/** Só descarta a sessão local quando a credencial é realmente inválida. */
export function shouldClearLocalSession(error: unknown): boolean {
  return classifyAuthError(error) === "credential";
}

function readLastRefresh(): number {
  try {
    return Number(localStorage.getItem(LAST_REFRESH_KEY) || 0) || 0;
  } catch {
    return 0;
  }
}

function writeLastRefresh(ts: number) {
  try {
    localStorage.setItem(LAST_REFRESH_KEY, String(ts));
  } catch {
    /* ignore */
  }
}

function isExpiring(session: Session | null): boolean {
  if (!session) return true;
  if (!session.expires_at) return false;
  return session.expires_at - EXPIRY_MARGIN_S <= Math.floor(Date.now() / 1000);
}

async function withLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = (typeof navigator !== "undefined" ? navigator.locks : undefined) as
    | LockManager
    | undefined;
  if (!locks?.request) return fn();
  try {
    return await locks.request(LOCK_NAME, fn);
  } catch {
    return fn();
  }
}

async function doRefresh(): Promise<Session | null> {
  return withLock(async () => {
    // Outra aba pode ter renovado enquanto esperávamos a trava.
    const { data: current } = await supabase.auth.getSession();
    const last = readLastRefresh();
    if (current.session && (!isExpiring(current.session) || Date.now() - last < MIN_REFRESH_INTERVAL_MS)) {
      return current.session;
    }

    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      const kind = classifyAuthError(error);
      if (kind === "rate_limit") {
        backoffUntil = Date.now() + 30_000;
        console.warn("[auth-refresh] 429 — aguardando antes de tentar novamente");
        return current.session ?? null;
      }
      if (kind === "credential") return null;
      // rede/desconhecido: mantém a sessão atual
      return current.session ?? null;
    }

    writeLastRefresh(Date.now());
    return data.session ?? null;
  });
}

/**
 * Retorna uma sessão válida, renovando somente quando necessário.
 * @param force renova mesmo que ainda não esteja perto de expirar (respeita o intervalo mínimo e o backoff).
 */
export async function ensureFreshSession(force = false): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  const session = data.session ?? null;

  const needsRefresh = force || isExpiring(session);
  if (!needsRefresh) return session;

  const now = Date.now();
  if (now < backoffUntil) return session;
  if (session && !isExpiring(session) && now - readLastRefresh() < MIN_REFRESH_INTERVAL_MS) {
    return session;
  }

  if (!inFlight) {
    inFlight = doRefresh().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** Token pronto para uso em chamadas autenticadas. */
export async function getFreshAccessToken(force = false): Promise<string | null> {
  const session = await ensureFreshSession(force);
  return session?.access_token ?? null;
}
